import type { Path } from "@/router";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import MetricGrid from "@/components/MetricGrid";
import SectionHeading from "@/components/SectionHeading";
import SubSectionHeading from "@/components/SubSectionHeading";

import type { CareerPlanCompetencyScore } from "./careerPlanHistory";
import { getArchetypeStrengthScores, type ExperienceArchetype } from "./experienceIndexArchetypes";
import { getExperienceIndexStrengthCopy } from "./experienceIndexCompetencyCopy";
import { getExperienceIndexCompetencyIcon } from "./experienceIndexIcons";

const ARCHETYPE_HEADING = {
  heading: "Archetype",
  body: "Named profile from this person's completed answers. Independent of comparison role."
};

const TOP_COMPETENCIES_HEADING = "Top Competencies";

function ArchetypeProfile({ archetype }: { archetype: ExperienceArchetype }) {
  return (
    <div className="rounded-lg border border-sys-brand-outline bg-sys-brand-surface p-5">
      <p
        className="text-headline-small font-serif text-sys-brand-on-surface"
        data-testid="career-plan-ei-archetype-title"
      >
        {archetype.title}
      </p>
      <p className="mt-2 text-body-medium text-sys-brand-on-surface" data-testid="career-plan-ei-archetype-growth-edge">
        <span className="text-body-medium-semibold">Growth Edge:</span> {archetype.growthEdgeName}
      </p>
      <p
        className="mt-2 text-body-medium text-sys-brand-on-surface-variant"
        data-testid="career-plan-ei-archetype-description"
      >
        {archetype.description}
      </p>
    </div>
  );
}

export default function ExperienceIndexArchetypeSection({
  archetype,
  scores,
  onSelectCompetency
}: {
  archetype: ExperienceArchetype;
  scores: CareerPlanCompetencyScore[];
  onSelectCompetency?: (competencyId: string) => void;
}) {
  const strengths = getArchetypeStrengthScores(archetype, scores);
  const metrics: MetricCardType<Path>[] = strengths.map((strength) => ({
    iconName: getExperienceIndexCompetencyIcon(strength.competencyName),
    value: strength.competencyName,
    label: getExperienceIndexStrengthCopy(strength.competencyName),
    testId: `career-plan-ei-archetype-strength-${strength.competencyId}`,
    asButton: Boolean(onSelectCompetency),
    onClick: onSelectCompetency ? () => onSelectCompetency(strength.competencyId) : undefined
  }));

  return (
    <div data-testid="career-plan-ei-archetype">
      <SectionHeading heading={ARCHETYPE_HEADING.heading} body={ARCHETYPE_HEADING.body} />
      <div className="flex flex-col gap-6">
        <ArchetypeProfile archetype={archetype} />
        {metrics.length > 0 ? (
          <div className="flex flex-col gap-2" data-testid="career-plan-ei-archetype-strengths">
            <SubSectionHeading>{TOP_COMPETENCIES_HEADING}</SubSectionHeading>
            <MetricGrid metrics={metrics} layout="compact" hasHeading />
          </div>
        ) : null}
      </div>
    </div>
  );
}
